/**
 * Constants for ticket selection and multi-ticket warning messages
 */

export const TICKET_MESSAGES = {
  // Multi-ticket warning modal
  MULTI_TICKET_WARNING: {
    TITLE: 'Multiple Tickets Selected',
    PURCHASING: (quantity: number, ticketName: string) => 
      `You're purchasing ${quantity} ${ticketName} tickets.`,
    NEXT_STEP_INFO: "You'll be asked to provide details for each attendee in the next step.",
    CANCEL: 'Cancel',
    CONTINUE: 'I Understand, Continue',
  },

  // Warning messages by audience type
  AUDIENCE_WARNINGS: {
    ANYONE: "You're purchasing multiple tickets. People without Tradewing accounts will receive an invite email to claim their ticket.",
    ANY_LOGGED_IN_USER: "You're purchasing multiple tickets. People without Tradewing accounts will receive an invite email to claim their ticket.",
    MEMBERS_ONLY: "This ticket is for members only. You can assign tickets only to members.",
    SPECIFIC_GROUPS: "Restricted to members of specific groups. You can assign tickets only to people in these groups.",
    ADMINS_ONLY: "This ticket is for admins only. You can assign tickets only to admins.",
    SPECIFIC_PEOPLE: "This ticket is limited to specific people. You can assign tickets only to people on the approved list.",
    ANYONE_WITH_TICKET_LINK: "You're purchasing multiple tickets. You'll be able to assign them to anyone.",
    DEFAULT: "You're purchasing multiple tickets. Please ensure all attendees meet the ticket requirements.",
  },

  // Purchaser participation modal
  PURCHASER_PARTICIPATION: {
    TITLE: 'Are you one of the guests?',
    DESCRIPTION: 'Would you like to register yourself as one of the attendees for this event?',
    YES: 'Yes, register me',
    NO: 'No, I\'m just purchasing',
    CANCEL: 'Cancel',
  },

  PURCHASER_PARTICIPATION_ENROLLMENT: {
    TITLE: 'Are you one of the guests?',
    DESCRIPTION:
      'Would you like to enroll yourself as one of the attendees for this course?',
    YES: 'Yes, enroll me',
    NO: 'No, I\'m just purchasing',
    CANCEL: 'Cancel',
  },

  // Eligibility messages
  ELIGIBILITY: {
    SIGN_IN_REQUIRED: 'Please sign in or create an account to continue.',
    MEMBERS_ONLY: 'This event is for members only.',
    GROUP_MEMBERS_ONLY: 'This event is restricted to members of specific groups.',
    SPECIFIC_PEOPLE_ONLY: 'This event is limited to specific people.',
    TICKET_RESTRICTED_GROUPS: 'This ticket is restricted to members of specific groups.',
    TICKET_MEMBERS_GROUP: 'This ticket is available only to members who are part of the specified group.',
    TICKET_ADMINS_GROUP: 'This ticket is restricted to Event Admins and Association Admins who are part of the specified group.',
    TICKET_MEMBERS_LOGGED_IN: 'This ticket is available only to members. Please add a membership to the cart before purchasing the ticket.',
    TICKET_ADMINS_LOGGED_IN: 'This ticket is restricted to Event Admins and Association Admins only.',
    TICKET_ADMINS_MEMBERS: 'This ticket is restricted to Event Admins and Association Admins who are also members.',
    TICKET_ADMINS_SPECIFIC: 'This ticket is restricted to Event Admins and Association Admins who are among the specific people selected for this event.',
    TICKET_MEMBERS_PRICING: 'This ticket pricing is available only to members. Please add a membership to the cart before purchasing the ticket.',
    TICKET_ADMINS_ONLY: 'This ticket is for admins only.',
    TICKET_RESTRICTED_GROUPS_GENERAL: 'This ticket is restricted to members of specific groups.',
    TICKET_SPECIFIC_PEOPLE: 'This ticket is limited to specific people on the approved list.',
  },

  // Toast messages
  TOAST: {
    CANNOT_ADD_MULTIPLE: {
      TITLE: 'Cannot add multiple tickets',
      DESCRIPTION: 'Ticket link tickets can only be purchased for yourself (quantity: 1).',
    },
  },

  // Info messages
  INFO: {
    MULTI_TICKET: (quantity: number) => 
      `You're purchasing ${quantity} tickets. You'll be asked to provide details for each attendee in the next step.`,
  },
} as const;

/**
 * Get warning message for a specific audience type
 */
export const getWarningMessage = (audienceType: string): string => {
  switch (audienceType) {
    case 'Anyone':
    case 'Any Logged-in User':
      return TICKET_MESSAGES.AUDIENCE_WARNINGS.ANYONE;
    
    case 'Members Only':
      return TICKET_MESSAGES.AUDIENCE_WARNINGS.MEMBERS_ONLY;
    
    case 'Specific Groups':
      return TICKET_MESSAGES.AUDIENCE_WARNINGS.SPECIFIC_GROUPS;
    
    case 'Admins Only':
      return TICKET_MESSAGES.AUDIENCE_WARNINGS.ADMINS_ONLY;
    
    case 'Specific People':
      return TICKET_MESSAGES.AUDIENCE_WARNINGS.SPECIFIC_PEOPLE;
    
    case 'Anyone with Ticket Link':
      return TICKET_MESSAGES.AUDIENCE_WARNINGS.ANYONE_WITH_TICKET_LINK;
    
    default:
      return TICKET_MESSAGES.AUDIENCE_WARNINGS.DEFAULT;
  }
};

